// @flow
import type {
    CurrentAddressType,
    CustomerType,
    ShippingMethodType,
} from 'constants/flow/flowTypesCheckout';
import type {SuccessDataForGlobalDataLayerType} from 'constants/flow/flowTypesGlobalDataLayer';
import type {QuoteItemType, QuotePreparedItemType} from 'constants/flow/flowTypesCart';

import type {OrderDataType} from 'pages/checkout/store/reducers/orderData/orderDataType';
import type {CheckoutTotalsType, CheckoutType} from 'reducers/checkout/checkoutType';

import {emptyStringIsFalsy, getAttribute} from '../../helpers';
import {getBadge} from '../cart/cart';

type OrderTotalsType = {
    coupon_discount_amount: number,
    coupon_used: string,
    subtotal: string,
    total_withshipping: string,
    total_noshipping: string,
};

type CustomerForSuccessType = {
    first_name: string,
    last_name: string,
    email: string,
};

/**
 * Get customer data from shipping data if customer data empty.
 * @param {CustomerType} customer - customer data
 * @param {CurrentAddressType} shippingAddress - shipping address data
 * @returns {CustomerForSuccessType} - customer information
 */
export function getCustomerForSuccessPage(
    customer: CustomerType,
    shippingAddress: CurrentAddressType,
): CustomerForSuccessType {
    return {
        first_name: customer.firstname || shippingAddress.data.firstName,
        last_name: customer.lastname || shippingAddress.data.lastName,
        email: customer.email || shippingAddress.data.email || '',
    };
}

/**
 * Save order details for price.
 *
 * @param {CheckoutTotalsType} totals - Cart total data.
 * @returns {OrderTotalsType} - Order details for price.
 */
export const getOrderTotals = (totals: CheckoutTotalsType): OrderTotalsType =>
    totals
        ? {
              coupon_discount_amount: Number(totals.discount),
              coupon_used: totals.appliedCoupons ? totals.appliedCoupons.join(' / ') : '',
              subtotal: parseFloat(totals.total).toFixed(4),
              total_withshipping: parseFloat(
                  Number(totals.subtotal) + Number(totals.shippingPrice),
              ).toFixed(4),
              total_noshipping: parseFloat(totals.subtotal).toFixed(4),
          }
        : {
              coupon_discount_amount: 0,
              coupon_used: '',
              subtotal: '0.0000',
              total_withshipping: '0.0000',
              total_noshipping: '0.0000',
          };

/**
 * Prepare quote item
 * @param {Array<QuoteItemType>} item - Quote item
 * @returns {QuotePreparedItemType} Prepared quote item
 */
export const prepareProductData = (item: QuoteItemType): QuotePreparedItemType => {
    return {
        attribute_set: getAttribute(item.type),
        brand: emptyStringIsFalsy(item.brand),
        color: item.frame ? emptyStringIsFalsy(item.frame.color) : '',
        frame_sku: emptyStringIsFalsy(item.product_sku),
        frame_url_path: emptyStringIsFalsy(item.frame_url_path),
        full_sku: emptyStringIsFalsy(item.sku),
        gender: emptyStringIsFalsy(item.gender),
        glasses_type: emptyStringIsFalsy(item.glasses_type),
        item_price: (item.frame && item.frame.price) || item.specialPrice || item.price || '',
        lens_type: emptyStringIsFalsy(item.lens_type),
        previous_category_id: emptyStringIsFalsy(item.previous_category_id),
        previous_category_name: emptyStringIsFalsy(item.previous_category_name),
        product_id: emptyStringIsFalsy(item.product_id),
        product_name: emptyStringIsFalsy(item.name),
        product_badge: getBadge(item.badges),
        quantity: Number(item.qty),
        size: item.frame ? emptyStringIsFalsy(item.frame.frameSize) : '',
        window_name: emptyStringIsFalsy(item.window_name),
        product_img: emptyStringIsFalsy(item.img),
    };
};

/**
 * Get fields for Global Data Layer for success page
 * @namespace
 * @param {Object} data - data
 * @property {CurrentAddressType} billingAddress - billing address data
 * @property {CurrentAddressType} shippingAddress - shipping address data
 * @property {CustomerType} customerState - customer data
 * @property {OrderDataType} orderData - order data
 * @property {string} currentBillingAddress - current billing address
 * @property {string} currentShippingAddress - current shipping address
 * @property {CheckoutType} checkout - checkout data
 * @property {PaymentType} paymentMethodActive - current payment method
 * @property {ShippingMethodType} shippingMethodActive - current shipping methods
 * @property {Array<QuoteItemType>} products - order review products
 * @returns {SuccessDataForGlobalDataLayerType} - success page global data layer data
 */
export function getSuccessDataGlobalDataLayer(data: {
    billingAddress: CurrentAddressType,
    shippingAddress: CurrentAddressType,
    customerState: CustomerType,
    orderData: OrderDataType,
    currentBillingAddress: string,
    currentShippingAddress: string,
    checkout: CheckoutType,
    paymentMethodActive: {type: string, code: string},
    shippingMethodActive: ShippingMethodType,
    products: Array<QuoteItemType>,
}): SuccessDataForGlobalDataLayerType {
    return {
        page_name: 'Success Page',
        page_type_param1: 'OTHER',
        page_type_param2: 'ORDER SUCCESS',
        products: data.products.map(prepareProductData),
        CC_type: emptyStringIsFalsy(data.paymentMethodActive.type),
        billing_address: data.billingAddress.data.street
            ? data.currentBillingAddress
            : data.currentShippingAddress,
        shipping_address: data.currentShippingAddress,
        payment_method: emptyStringIsFalsy(data.paymentMethodActive.code),
        shipping_method: `${emptyStringIsFalsy(data.shippingMethodActive.title)} - ${
            data.shippingMethodActive.description
        }`,
        shipping_revenue: parseFloat(data.checkout.totals.shippingPrice).toFixed(4),
        oid: data.orderData.orderId,
        segment: data.orderData.mentionMeSegment,
        store_view: window.location.origin,
        ...getOrderTotals(data.checkout.totals),
        ...getCustomerForSuccessPage(data.customerState, data.shippingAddress),
    };
}
